import React, { useMemo, useState } from "react";
import { Bell, MessageSquare, AlertTriangle, ClipboardList, Clock, ArrowRight, Filter } from "lucide-react";
import { formatDistanceToNow, differenceInDays } from "date-fns";
import { useNotifications } from "../contexts/NotificationContext";
import { getEthiopicDateTime } from "utils/constantFunction";
import { useTranslation } from "contexts/i18nContext";
import { useNavigate } from "react-router-dom";
import { notificationText } from "utils/notificationText";
import { notificationRoute } from "utils/recordRoutes";

interface NotificationItem {
  id: string;
  type?: string;
  message?: string;
  timestamp?: string;
  is_read?: boolean;
  from_user?: {
    full_name?: string;
    employee_id?: string;
  };
  object_id?: string;
  related_id?: string;
}

const NotificationComponent: React.FC = () => {
  const { notifications, markAsRead } = useNotifications();
  const { t, language } = useTranslation();
  // Title and sentence in the selected language, from the backend's codes.
  // (This app's t() returns the key when missing, so statuses outside the
  // helper's list are humanized there instead.)
  const text = (n: any) => notificationText(n, (language as any) || "en");
  const navigate = useNavigate();
  const [filter, setFilter] = useState<"all" | "new" | "read">("all");

  const processedNotifications = useMemo(() => {
    const now = new Date();
    return [...notifications].sort((a, b) => {
      const dateA = a.timestamp ? new Date(a.timestamp).getTime() : 0;
      const dateB = b.timestamp ? new Date(b.timestamp).getTime() : 0;
      return dateB - dateA;
    }).map((notification) => {
      const notificationDate = notification.timestamp ? new Date(notification.timestamp) : null;
      const daysOld = notificationDate ? differenceInDays(now, notificationDate) : null;
      const requiresAction = daysOld !== null && daysOld <= 3 && !notification.is_read;
      
      return {
        ...notification,
        requiresAction,
        daysOld,
      };
    });
  }, [notifications]);

  const newNotifications = processedNotifications.filter((n) => n.requiresAction);
  const olderNotifications = processedNotifications.filter((n) => !n.requiresAction);

  const filteredNotifications = useMemo(() => {
    switch (filter) {
      case "new":
        return processedNotifications.filter((n) => !n.is_read);
      case "read":
        return processedNotifications.filter((n) => n.is_read);
      default:
        return processedNotifications;
    }
  }, [processedNotifications, filter]);

  const newFiltered = filteredNotifications.filter((n) => n.requiresAction);
  const olderFiltered = filteredNotifications.filter((n) => !n.requiresAction);

  const handleClick = (notification: NotificationItem & { requiresAction?: boolean; daysOld?: number | null }) => {
    if (!notification.is_read) {
      markAsRead(notification.id);
    }

    // Open the record's page: the backend sends which page and the
    // complaint's id; appeal pages also get whether it is regional.
    const route = notificationRoute(notification);
    if (route) navigate(route[0], route[1] ? { state: route[1] } : undefined);
  };

  const getNotificationIcon = (type?: string) => {
    switch (type) {
      case "case_assignment":
        return <ClipboardList className="w-5 h-5 text-blue-600" />;
      case "new_message":
        return <MessageSquare className="w-5 h-5 text-green-600" />;
      case "system_alert":
        return <AlertTriangle className="w-5 h-5 text-red-600" />;
      default:
        return <Bell className="w-5 h-5 text-gray-600" />;
    }
  };

  const getNotificationTypeLabel = (type?: string) => {
    switch (type) {
      case "case_assignment":
        return "Case Assignment";
      case "new_message":
        return "New Message";
      case "system_alert":
        return "System Alert";
      default:
        return "Notification";
    }
  };

  const getStatusBadge = (isRead: boolean, type?: string) => {
    if (isRead) {
      return (
        <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-600 border border-gray-200">
          Read
        </span>
      );
    }

    // Color code by notification type for unread
    const typeColors: Record<string, { bg: string; text: string; border: string; dot: string }> = {
      case_assignment: {
        bg: "bg-blue-50",
        text: "text-blue-700",
        border: "border-blue-200",
        dot: "bg-blue-600"
      },
      new_message: {
        bg: "bg-green-50",
        text: "text-green-700",
        border: "border-green-200",
        dot: "bg-green-600"
      },
      system_alert: {
        bg: "bg-red-50",
        text: "text-red-700",
        border: "border-red-200",
        dot: "bg-red-600"
      },
      default: {
        bg: "bg-amber-50",
        text: "text-amber-700",
        border: "border-amber-200",
        dot: "bg-amber-600"
      }
    };

    const colors = typeColors[type || "default"] || typeColors.default;

    return (
      <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${colors.bg} ${colors.text} ${colors.border} flex items-center gap-1.5`}>
        <span className={`w-1.5 h-1.5 ${colors.dot} rounded-full animate-pulse`} />
        New
      </span>
    );
  };

if (processedNotifications.length === 0) {
    return (
      <div className="w-96 p-8 text-center bg-white border border-gray-200 shadow-lg rounded-xl">
        <div className="flex flex-col items-center">
          <div className="p-3 rounded-full bg-gray-50 mb-3">
            <Bell className="w-6 h-6 text-gray-400" />
          </div>
          <p className="text-gray-600 font-medium">No new notifications</p>
        </div>
      </div>
    );
  }

  const renderNotificationItem = (
    notification: NotificationItem & { requiresAction?: boolean; daysOld?: number | null },
    showActionBadge: boolean = false
  ) => {
    const typeColors: Record<string, { bg: string; iconBg: string; border: string }> = {
      case_assignment: {
        bg: "bg-blue-50/40",
        iconBg: "bg-blue-100",
        border: "border-l-blue-500"
      },
      new_message: {
        bg: "bg-green-50/40",
        iconBg: "bg-green-100",
        border: "border-l-green-500"
      },
      system_alert: {
        bg: "bg-red-50/40",
        iconBg: "bg-red-100",
        border: "border-l-red-500"
      },
      default: {
        bg: "bg-amber-50/40",
        iconBg: "bg-amber-100",
        border: "border-l-amber-500"
      }
    };

    const colors = typeColors[notification.type || "default"] || typeColors.default;
    const objectId = notification.object_id || notification.related_id;

    return (
      <div
        key={notification.id}
        data-testid={`notification-${notification.id}`}
        className={`flex flex-col p-4 border-b border-gray-100 hover:bg-gray-50 transition-all duration-200 cursor-pointer group relative
          ${notification.is_read ? "bg-white" : colors.bg}
          ${notification.is_read ? "border-l-4 border-l-transparent" : `border-l-4 ${colors.border}`}
        `}
        onClick={() => handleClick(notification)}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2.5 flex-1 min-w-0">
            <div className={`p-1.5 rounded-lg ${notification.is_read ? "bg-gray-100" : colors.iconBg}`}>
              {getNotificationIcon(notification.type)}
            </div>
            <div className="flex flex-col min-w-0 flex-1">
              <h3 className="text-sm font-semibold text-gray-900 truncate">
                {text(notification).title || getNotificationTypeLabel(notification.type)}
              </h3>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {getStatusBadge(notification.is_read ?? false, notification.type)}
          </div>
        </div>

        <p className="text-gray-700 text-sm mt-3 leading-relaxed line-clamp-2">
          {text(notification).message?.replace(/_/g, " ")}
        </p>

        <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
          <div className="flex items-center gap-2">
            <div className="p-1 rounded-full bg-white border border-gray-200">
              <Clock className="w-3 h-3 text-gray-400" />
            </div>
            <span className="text-xs text-gray-500 font-medium">
              {notification.timestamp && getEthiopicDateTime({
                datetime: new Date(notification.timestamp),
                displayHour: true,
              })}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {(notification.daysOld != null) && notification.daysOld > 0 && (
              <span className="text-xs text-gray-400">
                {notification.daysOld}d ago
              </span>
            )}
            {objectId && (
              <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-blue-600 transition-colors" />
            )}
          </div>
        </div>

        {!notification.is_read && (
          <div className="absolute inset-0 border-2 border-blue-400/15 pointer-events-none rounded-lg" />
        )}
      </div>
    );
  };

  return (
    <div className="w-96 bg-white border border-gray-200 shadow-lg rounded-xl overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-base font-bold text-gray-800 flex items-center gap-2">
              <Bell className="w-5 h-5 text-red-600 fill-red-50" />
              Notifications
            </h3>
            <p className="text-xs text-gray-500 mt-1">
              {newFiltered.length} require action • {olderFiltered.length} older
            </p>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setFilter("all")}
              className={`px-2 py-1 text-xs rounded-md transition-colors ${
                filter === "all" 
                  ? "bg-gray-800 text-white" 
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              All
            </button>
            <button
              onClick={() => setFilter("new")}
              className={`px-2 py-1 text-xs rounded-md transition-colors ${
                filter === "new" 
                  ? "bg-red-600 text-white" 
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              New
            </button>
            <button
              onClick={() => setFilter("read")}
              className={`px-2 py-1 text-xs rounded-md transition-colors flex items-center gap-1 ${
                filter === "read" 
                  ? "bg-green-600 text-white" 
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              <Clock className="w-3 h-3" />
              Read
            </button>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-0 max-h-96 overflow-y-auto bg-gray-50/30">
        {filter === "all" ? (
          <>
            {newFiltered.length > 0 && (
              <>
                <div className="px-4 py-2 bg-red-50 border-b border-red-100 sticky top-0">
                  <p className="text-xs font-semibold text-red-700 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    Requires Action ({newFiltered.length})
                  </p>
                </div>
                {newFiltered.map((notification) => renderNotificationItem(notification, true))}
              </>
            )}

            {olderFiltered.length > 0 && (
              <>
                <div className="px-4 py-2 bg-gray-50 border-b border-gray-100 sticky top-0">
                  <p className="text-xs font-medium text-gray-600">
                    All Notifications ({olderFiltered.length})
                  </p>
                </div>
                {olderFiltered.map((notification) => renderNotificationItem(notification, false))}
              </>
            )}
          </>
        ) : (
          <>
            {/* All unread ones, not only those under 3 days old. */}
            {filter === "new" && filteredNotifications.map((notification) => renderNotificationItem(notification, true))}
            {filter === "read" && olderFiltered.map((notification) => renderNotificationItem(notification, false))}
          </>
        )}

        {filteredNotifications.length === 0 && (
          <div className="p-8 text-center text-gray-500">
            <Bell className="w-8 h-8 mx-auto mb-2 text-gray-300" />
            <p className="text-sm">No {filter === "all" ? "" : filter} notifications</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationComponent;